import { styled as styled, CSSObject, alpha } from '@mui/material/styles';

import {
  TableContainerProps as MuiTableContainerProps,
  TableProps as MuiTableProps,
  TableCellProps as MuiTableCellProps,
  TableHeadProps as MuiTableHeadProps,
  TableBodyProps as MuiTableBodyProps,
  TableSortLabelProps as MuiTableSortLabelProps,
  TableContainer,
  Table,
  TableCell,
  TableSortLabel,
  TableRow,
  TableHead,
  TableBody,
  tableCellClasses
} from '@mui/material';
import { TableProps, StyledTableRowProps } from '../types';

type ExtendedTableProps = TableProps & MuiTableProps;
type ExtendedTableContainerProps = TableProps & MuiTableContainerProps;

export const StyledTableContainer = styled(TableContainer)<ExtendedTableContainerProps>(
  ({ theme, color }) => ({})
);

export const StyledTable = styled(Table, {
  shouldForwardProp: (prop) => !['colorHead', 'checked', 'size'].includes(String(prop))
})<ExtendedTableProps>(({ theme, color, colorHead, checked, size }) => ({
  ...(colorHead && {
    '.MuiTableCell-head': {
      backgroundColor: `#f6f7f9`,
      color: theme.palette.common.black
    }
  }),
  ...(size === 'small' && {
    '.MuiTableCell-body': {
      padding: `8px 16px 8px 16px`
    }
  }),
  ...(size === 'large' && {
    '.MuiTableCell-body': {
      padding: `16px`
    }
  })
}));

export const StyledTableHead = styled(TableHead)<MuiTableHeadProps>(({ theme, color }) => ({}));
export const StyledTableBody = styled(TableBody)<MuiTableBodyProps>(({ theme, color }) => ({}));
export const StyledTableSortLabel = styled(TableSortLabel)<MuiTableSortLabelProps>(
  ({ theme, color }) => ({})
);

const colorMixin = ({ theme, color }: any): CSSObject => ({
  ...(color === `red` && {
    backgroundColor: theme.palette.error[50],
    [`&:hover`]: {
      backgroundColor: theme.palette.error[100]
    }
  }),
  ...(color === `yellow` && {
    backgroundColor: theme.palette.yellow[50],
    [`&:hover`]: {
      backgroundColor: theme.palette.yellow[100]
    }
  }),
  ...(color === `grey` && {
    backgroundColor: theme.palette.grey[50],
    [`&:hover`]: {
      backgroundColor: theme.palette.grey[100]
    }
  })
});

const checkedMixin = ({ theme, checked }: any): CSSObject => ({
  ...(checked && {
    backgroundColor: alpha(theme.palette.grayscaleSecondary.A100, 0.24),
    [`&:hover`]: {
      backgroundColor: alpha(theme.palette.grayscaleSecondary.A100, 0.36)
    }
  })
});

export const StyledTableRow = styled(TableRow)<StyledTableRowProps>(
  ({ theme, color, checked, selected }) => ({
    [`&:hover`]: {
      backgroundColor: alpha(theme.palette.secondary[50], 0.24)
    },
    '&.Mui-selected': {
      backgroundColor: alpha(theme.palette.grayscaleSecondary.A100, 0.36),
      [`&:hover`]: {
        backgroundColor: alpha(theme.palette.grayscaleSecondary.A100, 0.48)
      }
    },
    ...(color !== undefined && color !== `inherit` && colorMixin({ theme, color })),
    ...(checked !== undefined && checkedMixin({ theme, checked }))
  })
);

export const StyledTableCell = styled(TableCell)<MuiTableCellProps>(({ theme, color }) => ({
  borderColor: theme.palette.secondary[100],
  padding: `12px 16px 12px 16px`,
  fontSize: `14px`,
  [`&.${tableCellClasses.head}`]: {
    padding: `8px 16px 8px 16px`,
    backgroundColor: theme.palette.background.paper,
    fontWeight: `500`,
    color: theme.palette.secondary[800],
    borderTop: `1px solid ${theme.palette.secondary[100]}`,
    borderBottom: `1px solid ${theme.palette.secondary[100]}`,
    boxShadow: `0 2px 0 0 rgba(0, 0, 0, 0.05)`,
    '.DropdownIndicator': {
      opacity: 0
    },
    '&:hover': {
      backgroundColor: `#fcfcfd`,
      '.DropdownIndicator': {
        opacity: 1
      }
    },
    '&[aria-sort]': {
      '.DropdownIndicator': {
        opacity: 1
      }
    }
  },
  [`&.${tableCellClasses.body}`]: {
    color: theme.palette.secondary[800],
    '&:has(.MuiCheckbox-root)': {
      paddingLeft: `8px`
    },
    '.MuiCheckbox-root': {
      marginRight: `8px`
    }
  },
  [`&.MuiTableCell-head.MuiTableCell-alignRight`]: {
    flexDirection: `row`,
    [`.MuiTableSortLabel-root`]: {
      justifyContent: `flex-end`
    }
  }
}));
